import { ChevronDownIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Fade,
  Flex,
  FormLabel,
  HStack,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import exportFromJSON from 'export-from-json';
import { useEffect, useState } from 'react';
import { HiPlusCircle } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { Column } from 'react-table';

import { InteractiveTable } from '../../../components/data/InteractiveTable';
import { ValueCard } from '../../../components/data/ValueCard';
import { apiSensedia } from '../../../services/api-sensedia';

type Table = {
  codigoVendedor: string;
  nomeVendedor: string;
  valorAcumuladoVendas: number;
  quantidadeVendasEfetivadas: number;
  quantidadeVendasNaoEfetivadas: number;
};

type Data = {
  acumulados?: {
    valoresAcumulados: number;
    quantidadeVendasEfetivadas: number;
    quantidadeVendasNaoEfetivadas: number;
    quantidadeVendasGeral: number;
  };
  tabela: Table[];
  logins: string[];
};

export const ContrataFacil = (): JSX.Element => {
  const toast = useToast();
  const navigate = useNavigate();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  const [data, setData] = useState<Data>({
    acumulados: {
      valoresAcumulados: 0,
      quantidadeVendasEfetivadas: 0,
      quantidadeVendasNaoEfetivadas: 0,
      quantidadeVendasGeral: 0
    },
    tabela: [
      {
        codigoVendedor: '',
        nomeVendedor: '',
        valorAcumuladoVendas: 0,
        quantidadeVendasEfetivadas: 0,
        quantidadeVendasNaoEfetivadas: 0
      }
    ],
    logins: ['']
  });

  const [table, setTable] = useState<Table[]>([
    {
      codigoVendedor: '',
      nomeVendedor: '',
      valorAcumuladoVendas: 0,
      quantidadeVendasEfetivadas: 0,
      quantidadeVendasNaoEfetivadas: 0
    }
  ]);

  const [itemsCount, setItemsCount] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(5);

  const [initialDate, setInitialDate] = useState('');
  const [finalDate, setFinalDate] = useState('');
  const [userFilter, setUserFilter] = useState('');

  const columns: Column<object>[] = [
    {
      Header: 'Código do vendedor',
      accessor: 'codigoVendedor'
    },
    {
      Header: 'Nome do vendedor',
      accessor: 'nomeVendedor'
    },
    {
      Header: 'Valores acumulados por vendedor',
      accessor: 'valorAcumuladoVendas',
      Cell: ({
        row
      }: {
        row: { original: { valorAcumuladoVendas: number } };
      }) => (
        <Text>
          R${' '}
          {parseFloat(
            row.original.valorAcumuladoVendas.toFixed(2)
          ).toLocaleString('PT-BR')}
        </Text>
      )
    },
    {
      Header: 'Quantidade de vendas não efetuadas',
      accessor: 'quantidadeVendasNaoEfetivadas'
    },
    {
      Header: 'Quantidade de vendas efetuadas',
      accessor: 'quantidadeVendasEfetivadas'
    }
  ];

  const handleFilterLast30days = async () => {
    setInitialDate('');
    setFinalDate('');
    setUserFilter('');
    setLoadingTable(true);

    await getData();

    setLoadingTable(false);
  };

  const handleFilter = async () => {
    setLoadingTable(true);

    if ((initialDate && !finalDate) || (!initialDate && finalDate)) {
      toast({
        title: 'Atenção',
        description: 'Favor verificar o preenchimento das datas',
        position: 'top',
        status: 'warning',
        duration: 9000,
        isClosable: true
      });

      setLoadingTable(false);

      return;
    }

    if (initialDate && finalDate) {
      const dataInicial = initialDate.replaceAll('-', '/');
      const dataFim = finalDate.replaceAll('-', '/');

      await getData(dataInicial, dataFim);

      setLoadingTable(false);
    } else await getData();

    if (userFilter.length > 0) {
      const newTable = data.tabela.filter((item) => {
        return `${item.codigoVendedor}/${item.nomeVendedor}` === userFilter;
      });

      setTable(newTable);
      setItemsCount(newTable.length > 10 ? 10 : newTable.length);
      setLoadingTable(false);
    }
  };

  const handleClearFilters = async () => {
    setLoading(true);

    setInitialDate('');
    setFinalDate('');
    setUserFilter('');

    try {
      const token = localStorage.getItem('tokenPortal');

      const response = await apiSensedia.get(
        `/gestao-de-carteiras/contrata-facil/vendas`,
        {
          timeout: 0
        }
      );

      if (response.data.data.tabela) {
        setData(response.data.data);
        setTable(response.data.data.tabela);
        setItemsCount(
          response.data.data.tabela.length > 10
            ? 10
            : response.data.data.tabela.length
        );
      }

      setLoading(false);
      setLoadingTable(false);
    } catch (e: any) {
      if (e.response.status === 401) {
        onOpen();
      } else {
        const errorObj = e?.toJSON();
        const hasStatus = errorObj?.status || e?.response?.data?.statusCode;
        const hasMessage = errorObj?.message || e?.response?.data?.message;
        toast({
          position: 'top',
          description: (
            <Box>
              <Text>
                {' '}
                Ocorreu um erro. Esvazie o cache do navegador, aguarde alguns
                minutos e tente fazer login novamente, se persistir, por favor
                entrar em contato.
              </Text>
              <Text>
                {hasStatus && (
                  <>
                    <br />
                    <b>Detalhes:</b>
                    <br />
                  </>
                )}
                {hasStatus && (
                  <>{errorObj.status ?? e.response.data.statusCode}</>
                )}
                {hasStatus && hasMessage && ' - '}
                {hasMessage && (
                  <>{e.response.data.message ?? errorObj.message}</>
                )}
              </Text>
            </Box>
          ),
          duration: 15000,
          isClosable: true,
          status: 'error'
        });
      }

      setLoading(false);
    }

    setLoading(false);
  };

  const getData = async (dataInicio?: string, dataFim?: string) => {
    setLoading(true);

    try {
      const token = localStorage.getItem('tokenPortal');

      const response =
        dataInicio && dataFim
          ? await apiSensedia.get(
              `/gestao-de-carteiras/contrata-facil/vendas?dataInicio=${dataInicio}&dataFim=${dataFim}`,
              {
                timeout: 0
              }
            )
          : await apiSensedia.get(
              `/gestao-de-carteiras/contrata-facil/vendas`,
              {
                timeout: 0
              }
            );

      if (response.data.data.tabela) {
        setData(response.data.data);

        setTable(response.data.data.tabela);
        setItemsCount(
          response.data.data.tabela.length > 10
            ? 10
            : response.data.data.tabela.length
        );
      }

      setLoading(false);
      setLoadingTable(false);
    } catch (e: any) {
      if (e.response.status === 401) {
        onOpen();
      } else {
        const errorObj = e?.toJSON();
        const hasStatus = errorObj?.status || e?.response?.data?.statusCode;
        const hasMessage = errorObj?.message || e?.response?.data?.message;
        toast({
          position: 'top',
          description: (
            <Box>
              <Text>
                {' '}
                Ocorreu um erro. Esvazie o cache do navegador, aguarde alguns
                minutos e tente fazer login novamente, se persistir, por favor
                entrar em contato.
              </Text>
              <Text>
                {hasStatus && (
                  <>
                    <br />
                    <b>Detalhes:</b>
                    <br />
                  </>
                )}
                {hasStatus && (
                  <>{errorObj.status ?? e.response.data.statusCode}</>
                )}
                {hasStatus && hasMessage && ' - '}
                {hasMessage && (
                  <>{e.response.data.message ?? errorObj.message}</>
                )}
              </Text>
            </Box>
          ),
          duration: 15000,
          isClosable: true,
          status: 'error'
        });
      }

      setLoading(false);
    }
  };

  const handleNavigate = (to: string) => {
    if (!initialDate && !finalDate && !userFilter) {
      return navigate(`base-clientes/${to}`);
    }

    const vendedor = userFilter && userFilter.split('/')[0];
    const urlParams = new URLSearchParams({
      dataInicio: initialDate,
      dataFim: finalDate,
      vendedor: vendedor
    });

    return navigate(`base-clientes/${to}?${urlParams.toString()}`);
  };

  useEffect(() => {
    getData();
  }, []);

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true
  });

  const exportData = (ext: 'csv' | 'xls') => {
    const data = formatDataEfetivado() as any;
    const fileName = `Dados`;
    const exportType = exportFromJSON.types[ext];

    exportFromJSON({ data, fileName, exportType });
  };

  const formatDataEfetivado = () => {
    return table?.map((item) => ({
      'Codigo do Vendedor': item.codigoVendedor,
      'Nome do Vendedor': item.nomeVendedor,
      'Valor Acumulado de Vendas': item.valorAcumuladoVendas,
      'Qtd Vendas Efetivadas': item.quantidadeVendasEfetivadas,
      'Qtd Vendas Nao Efetivadas': item.quantidadeVendasNaoEfetivadas
    }));
  };

  return (
    <Stack p="1.7vw 11.5vw" spacing={23}>
      <Stack spacing={7}>
        <Stack spacing={1}>
          <Link href="/corretor" w="4rem">
            <HStack spacing={0}>
              <ChevronLeftIcon />
              <Text fontSize="1rem" color="#696969" fontWeight="bold">
                Voltar
              </Text>
            </HStack>
          </Link>
          <Flex justifyContent="space-between" alignItems="center">
            <Text color="#00335E" fontWeight="bold" fontSize="1.2rem">
              Painel de Vendas Contrata Fácil
            </Text>
            <Menu>
              <MenuButton
                bg="none"
                variant="outline"
                border="1px"
                borderColor="#1B365D"
                color="#1B365D"
                fontSize="0.8rem"
                as={Button}
                rightIcon={<ChevronDownIcon />}
              >
                Exportar Dados
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => exportData('csv')}>
                  Baixar .csv
                </MenuItem>
                <MenuItem onClick={() => exportData('xls')}>
                  Baixar .xls
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Stack>

        <HStack
          bg="#fff"
          wrap="wrap"
          borderRadius="1rem"
          minH="4.3rem"
          spacing={39}
          justify={isWideVersion ? 'flex-start' : 'center'}
          flexDir={{ base: 'column', lg: 'row' }}
          pointerEvents={!loading ? 'all' : 'none'}
          opacity={!loading ? '1' : '0.4'}
        >
          <HStack
            flexDir={{ base: 'column', md: 'column', lg: 'row' }}
            m=".6rem !important"
          >
            <Text fontSize=".8rem" fontWeight="bold" color="#1B365D">
              Filtrar:
            </Text>
            <Button
              bg="#1B365D"
              color="#fff"
              fontSize=".7rem"
              w="5.7rem"
              h="2.1rem"
              onClick={handleFilterLast30days}
              _hover={{
                opacity: '.9'
              }}
            >
              Últimos 30 dias
            </Button>
            <Text fontSize=".8rem">ou</Text>

            <FormLabel fontSize=".7rem">Data Inicial</FormLabel>
            <Input
              color="#1B365D"
              value={initialDate}
              onChange={(e) => {
                setInitialDate(e.target.value);
              }}
              fontSize=".7rem"
              type="date"
              w="8.1rem"
              h="2.1rem"
              placeholder="Data Inicial"
            />
            <FormLabel fontSize=".7rem">Data Final</FormLabel>
            <Input
              color="#1B365D"
              value={finalDate}
              onChange={(e) => {
                setFinalDate(e.target.value);
              }}
              fontSize=".7rem"
              type="date"
              w="8.1rem"
              h="2.1rem"
              placeholder="Data Final"
            />
          </HStack>
          <HStack
            flexDir={{ base: 'column', md: 'column', lg: 'row' }}
            justifyContent="center"
            ml="0 !important"
            m=".6rem !important"
          >
            <Text fontSize=".8rem" fontWeight="bold" color="#1B365D">
              Usuários:
            </Text>
            <Select
              w="5.2rem"
              h="2.1rem"
              value={userFilter}
              fontSize=".7rem"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setUserFilter(e.target.value)
              }
            >
              <option value="">Todos</option>
              {data &&
                data.logins?.map((user) => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
            </Select>
            <Button
              bg="#FA6B25"
              color="#fff"
              fontSize=".7rem"
              w="5.2rem"
              h="2.1rem"
              isLoading={loading}
              onClick={handleFilter}
              m="0 !important"
              mr="0"
              _hover={{
                opacity: '.9'
              }}
            >
              Filtrar
            </Button>
            <Button
              bg="#ACACAC"
              color="#fff"
              fontSize=".7rem"
              w="5.2rem"
              h="2.1rem"
              onClick={handleClearFilters}
              m=".3rem !important"
              ml="0"
              _hover={{
                opacity: '.9'
              }}
            >
              Limpar Filtros
            </Button>
          </HStack>
        </HStack>
      </Stack>

      {loading ? (
        <Flex w="100%" justify="center">
          <Spinner />
        </Flex>
      ) : (
        <Fade in={!loading}>
          <Stack
            bg="#fff"
            color="#00335E"
            borderRadius="1rem"
            p="1.4rem 1.1rem .8rem"
          >
            <Flex justify="space-around" wrap="wrap">
              <ValueCard
                prefix="R$"
                fontSize="2rem"
                value={
                  data.acumulados?.valoresAcumulados
                    ? data.acumulados?.valoresAcumulados > 0
                      ? parseFloat(
                          data.acumulados.valoresAcumulados.toFixed(2)
                        ).toLocaleString('PT-BR')
                      : '0'
                    : '0'
                }
                description="Valores acumulados"
                info="(total da EV)"
              />
              <ValueCard
                value={data.acumulados?.quantidadeVendasEfetivadas.toString()}
                description="Quantidade geral vendas"
                info="(efetivados)"
                actionTitle="Acessar lista"
                action={() => handleNavigate('efetivado')}
              />
              <ValueCard
                value={data.acumulados?.quantidadeVendasNaoEfetivadas.toString()}
                description="Quantidade geral vendas"
                info="(não efetivados)"
                actionTitle="Acessar lista"
                action={() => handleNavigate('nao-efetivado')}
              />
              <ValueCard
                value={data.acumulados?.quantidadeVendasGeral.toString()}
                description="Quantidade geral"
                info="(Efetivados e Não Efetivados)"
              />
            </Flex>

            {loadingTable ? (
              <Flex w="100%" justify="center">
                <Spinner />
              </Flex>
            ) : (
              <InteractiveTable
                sort="quantidadeVendasEfetivadas"
                columns={columns}
                data={table}
                itemsCount={itemsCount}
              />
            )}
          </Stack>
          <Flex
            w="100%"
            justify="flex-end"
            fontSize=".9rem"
            align="center"
            mt="2.1rem"
          >
            <Flex
              w={{ base: '100%', lg: '60%' }}
              justify={{ base: 'center', lg: 'space-between' }}
              flexDir={{ base: 'column', lg: 'row' }}
            >
              {!loadingTable && (
                <>
                  <Flex
                    align="center"
                    cursor="pointer"
                    justify="center"
                    onClick={() => {
                      setItemsCount(
                        itemsToShow + itemsCount > table.length
                          ? table.length
                          : itemsToShow + itemsCount
                      );
                    }}
                  >
                    <HiPlusCircle size={26} color="#00335E" />
                    <Text>
                      Mais itens (exibindo {itemsCount}/{table.length})
                    </Text>
                  </Flex>
                  <Box>
                    <Select
                      border="1px solid #00335E !important"
                      borderRadius="1rem !important"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setItemsToShow(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>exibir mais 5 por vez</option>
                      <option value={10}>exibir mais 10 por vez</option>
                      <option value={20}>exibir mais 20 por vez</option>
                      <option value={40}>exibir mais 40 por vez</option>
                    </Select>
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </Fade>
      )}
      <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent textAlign="center">
          <ModalHeader>Atenção!</ModalHeader>
          <ModalBody>
            <Text>Ops, sua sessão expirou.</Text>
            <Text>
              Por favor, faça novamente o login no Portal do Corretor.
            </Text>
          </ModalBody>

          <ModalFooter justifyContent="center">
            <Link href={process.env.REACT_APP_PORTAL_URL}>
              <Button bg="#FA6B25" color="#fff" colorScheme="orange">
                Voltar
              </Button>
            </Link>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
